import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import ModuleGate from "../../components/ModuleGate";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  ArrowLeft,
  Target,
  Flame,
  Apple,
  Utensils,
  ChevronRight,
  Check,
  Clock,
  ChefHat,
  Calculator,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  RefreshCw,
  X,
  Plus
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import { getRecipeImage } from "../../utils/recipeImages";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores para desviaciones de macros
const getDeviationColor = (value, threshold) => {
  const absValue = Math.abs(value);
  if (absValue <= threshold.green) return "text-green-400";
  if (absValue <= threshold.yellow) return "text-yellow-400";
  return "text-red-400";
};

const getDeviationBg = (value, threshold) => {
  const absValue = Math.abs(value);
  if (absValue <= threshold.green) return "bg-green-500/20";
  if (absValue <= threshold.yellow) return "bg-yellow-500/20";
  return "bg-red-500/20";
};

// Thresholds para colores
const THRESHOLDS = {
  calories: { green: 100, yellow: 200 },
  protein: { green: 10, yellow: 20 },
  carbs: { green: 15, yellow: 30 },
  fat: { green: 8, yellow: 15 }
};

// Formatear ingrediente para mostrar cantidades legibles
const formatIngredientQuantity = (name, quantity, unit = "g") => {
  const foodLower = name.toLowerCase();
  
  // Redondear según tipo de alimento
  let roundedQty = quantity;
  
  // Carnes/pescados: múltiplos de 5 o 10g
  if (foodLower.includes("pollo") || foodLower.includes("ternera") || 
      foodLower.includes("pavo") || foodLower.includes("salmón") ||
      foodLower.includes("atún") || foodLower.includes("merluza")) {
    roundedQty = Math.round(quantity / 10) * 10;
    if (roundedQty < 50) roundedQty = 50; // mínimo 50g
  }
  // Arroz/pasta/avena: múltiplos de 5g
  else if (foodLower.includes("arroz") || foodLower.includes("pasta") || 
           foodLower.includes("avena") || foodLower.includes("quinoa")) {
    roundedQty = Math.round(quantity / 5) * 5;
    if (roundedQty < 30) roundedQty = 30; // mínimo 30g
  }
  // Aceite: múltiplos de 5ml
  else if (foodLower.includes("aceite")) {
    roundedQty = Math.round(quantity / 5) * 5;
    if (roundedQty < 5) roundedQty = 5;
    unit = "ml";
  }
  // Huevos: unidades
  else if (foodLower.includes("huevo") && !foodLower.includes("clara")) {
    const units = Math.round(quantity / 50);
    return `${units} ${units === 1 ? 'huevo' : 'huevos'}`;
  }
  // Default: redondear a enteros
  else {
    roundedQty = Math.round(quantity);
  }
  
  return `${roundedQty}${unit}`;
};

const MiNutricionPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [todayMeals, setTodayMeals] = useState(null);
  const [selectedMealSlot, setSelectedMealSlot] = useState(null);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeDetail, setShowRecipeDetail] = useState(false);
  const [calculatedRecipe, setCalculatedRecipe] = useState(null);
  const [savingSelection, setSavingSelection] = useState(false);

  // Cargar comidas del día
  const fetchTodayMeals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/premium/today-meals`,
        { headers: getAuthHeaders() }
      );
      setTodayMeals(response.data);
    } catch (error) {
      console.error("Error fetching today meals:", error);
      if (error.response?.status === 403) {
        toast.error("Necesitas plan de nutrición flexible configurado");
      } else {
        toast.error("Error al cargar tu plan nutricional");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTodayMeals();
  }, [fetchTodayMeals]);

  // Cargar recetas para un slot
  const fetchRecipesForSlot = async (slot) => {
    setLoadingRecipes(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/premium/recipes/for-meal/${slot}/filtered`,
        { headers: getAuthHeaders() }
      );
      setAvailableRecipes(response.data.recipes || []);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      toast.error("Error al cargar recetas");
    } finally {
      setLoadingRecipes(false);
    }
  };

  // Abrir selector de recetas
  const openRecipeSelector = (mealSlot) => {
    setSelectedMealSlot(mealSlot);
    setShowRecipeSelector(true);
    fetchRecipesForSlot(mealSlot.slot);
  };

  // Calcular receta ajustada
  const calculateRecipe = async (recipe) => {
    if (!selectedMealSlot?.target) {
      toast.error("No hay objetivos definidos para esta comida");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/premium/recipe-calculator`,
        {
          recipe_id: recipe.id,
          target_macros: selectedMealSlot.target,
          meal_slot: selectedMealSlot.slot
        },
        { headers: getAuthHeaders() }
      );
      
      setCalculatedRecipe(response.data);
      setSelectedRecipe(recipe);
      setShowRecipeDetail(true);
    } catch (error) {
      console.error("Error calculating recipe:", error);
      toast.error("Error al calcular la receta");
    }
  };

  // Guardar selección de receta
  const saveRecipeSelection = async () => {
    if (!calculatedRecipe || !selectedRecipe || !selectedMealSlot) return;
    
    setSavingSelection(true);
    try {
      await axios.post(
        `${API_URL}/api/premium/recipe-selection`,
        {
          meal_slot: selectedMealSlot.slot,
          recipe_id: selectedRecipe.id,
          recipe_name: selectedRecipe.nombre,
          imagen: selectedRecipe.imagen,
          adjusted_ingredients: calculatedRecipe.adjusted_ingredients,
          extra_foods: calculatedRecipe.extra_foods,
          has_extra_foods: calculatedRecipe.has_extra_foods,
          final_macros: calculatedRecipe.final_macros,
          target_macros: calculatedRecipe.target_macros,
          deviation: calculatedRecipe.deviation,
          fit_score: calculatedRecipe.fit_score,
          preparacion: calculatedRecipe.preparacion,
          tiempo_preparacion: calculatedRecipe.tiempo_preparacion || selectedRecipe.tiempo_preparacion,
          dificultad: calculatedRecipe.dificultad || selectedRecipe.dificultad,
          tips_goatgym: calculatedRecipe.tips_goatgym || selectedRecipe.tips_goatgym
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Receta guardada");
      setShowRecipeDetail(false);
      setShowRecipeSelector(false);
      setSelectedRecipe(null);
      setCalculatedRecipe(null);
      fetchTodayMeals(); // Recargar
    } catch (error) {
      console.error("Error saving selection:", error);
      toast.error("Error al guardar la receta");
    } finally {
      setSavingSelection(false);
    }
  };

  // Calcular resumen diario
  const dailySummary = useMemo(() => {
    if (!todayMeals) return null;
    
    const { daily_targets, daily_totals } = todayMeals;
    
    return {
      targets: daily_targets,
      totals: daily_totals,
      deviation: {
        calories: daily_totals.calories - (daily_targets.calories || 0),
        protein: daily_totals.protein - (daily_targets.protein || 0),
        carbs: daily_totals.carbs - (daily_targets.carbs || 0),
        fat: daily_totals.fat - (daily_targets.fat || 0)
      }
    };
  }, [todayMeals]);

  // Sin plan configurado
  if (!loading && (!todayMeals || !todayMeals.daily_targets?.calories)) {
    return (
      <ModuleGate module="premium_nutrition">
        <div className="min-h-screen bg-[#0B0B0B] p-4 pb-24">
          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-[#888] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-white">Mi Nutrición</h1>
          </div>
          
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-6 text-center">
              <Apple className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
              <h2 className="text-white text-lg font-bold mb-2">Plan nutricional no configurado</h2>
              <p className="text-[#888] text-sm">
                Tu entrenador aún no ha configurado tu plan de nutrición flexible.
                Contacta con él para que te asigne tus objetivos de kcal y macros.
              </p>
            </CardContent>
          </Card>
        </div>
      </ModuleGate>
    );
  }

  return (
    <ModuleGate module="premium_nutrition">
      <div className="min-h-screen bg-[#0B0B0B] p-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-[#888] hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-heading font-bold text-white">Mi Nutrición</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchTodayMeals}
            className="ml-auto text-[#888] hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            <div className="h-32 bg-[#1A1A1A] rounded-xl animate-pulse" />
            <div className="h-24 bg-[#1A1A1A] rounded-xl animate-pulse" />
            <div className="h-24 bg-[#1A1A1A] rounded-xl animate-pulse" />
          </div>
        ) : (
          <>
            {/* Objetivos Diarios */}
            <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#121212] border-[#D4AF37]/30 mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-[#D4AF37]" />
                  <h2 className="text-white font-bold">Objetivos Diarios</h2>
                </div>
                
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                    <Flame className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
                    <p className="text-white font-bold text-lg">{todayMeals?.daily_targets?.calories || 0}</p>
                    <p className="text-[#666] text-xs">kcal</p>
                  </div>
                  <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                    <span className="text-red-400 text-xs font-bold block mb-1">P</span>
                    <p className="text-white font-bold text-lg">{todayMeals?.daily_targets?.protein || 0}g</p>
                    <p className="text-[#666] text-xs">Proteína</p>
                  </div>
                  <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                    <span className="text-yellow-400 text-xs font-bold block mb-1">C</span>
                    <p className="text-white font-bold text-lg">{todayMeals?.daily_targets?.carbs || 0}g</p>
                    <p className="text-[#666] text-xs">Carbos</p>
                  </div>
                  <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                    <span className="text-blue-400 text-xs font-bold block mb-1">G</span>
                    <p className="text-white font-bold text-lg">{todayMeals?.daily_targets?.fat || 0}g</p>
                    <p className="text-[#666] text-xs">Grasas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Comidas */}
            <div className="space-y-3 mb-4">
              <h3 className="text-[#888] text-sm font-semibold flex items-center gap-2">
                <Utensils className="w-4 h-4" />
                Mis Comidas de Hoy
              </h3>
              
              {todayMeals?.meals?.map((meal) => (
                <Card 
                  key={meal.slot}
                  className={cn(
                    "bg-[#1A1A1A] border transition-all cursor-pointer",
                    meal.has_selection 
                      ? "border-green-500/30 hover:border-green-500/50" 
                      : "border-[#2A2A2A] hover:border-[#D4AF37]/50"
                  )}
                  onClick={() => openRecipeSelector(meal)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {meal.has_selection ? (
                          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Check className="w-4 h-4 text-green-400" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                            <ChefHat className="w-4 h-4 text-[#666]" />
                          </div>
                        )}
                        <div>
                          <h4 className="text-white font-semibold text-sm">{meal.slot_name}</h4>
                          {meal.has_selection && meal.selection?.recipe_name && (
                            <p className="text-[#D4AF37] text-xs">{meal.selection.recipe_name}</p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#666]" />
                    </div>
                    
                    {/* Objetivo de la comida */}
                    {meal.target && (
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-[#D4AF37]">{meal.target.calories} kcal</span>
                        <span className="text-red-400">{meal.target.protein}g P</span>
                        <span className="text-yellow-400">{meal.target.carbs}g C</span>
                        <span className="text-blue-400">{meal.target.fat}g G</span>
                      </div>
                    )}
                    
                    {/* Desviación si hay selección */}
                    {meal.has_selection && meal.selection?.deviation && (
                      <div className="mt-2 pt-2 border-t border-[#2A2A2A] flex items-center gap-3 text-xs">
                        <span className="text-[#666]">Diferencia:</span>
                        <span className={getDeviationColor(meal.selection.deviation.calories, THRESHOLDS.calories)}>
                          {meal.selection.deviation.calories > 0 ? '+' : ''}{meal.selection.deviation.calories} kcal
                        </span>
                        <span className={getDeviationColor(meal.selection.deviation.protein, THRESHOLDS.protein)}>
                          {meal.selection.deviation.protein > 0 ? '+' : ''}{meal.selection.deviation.protein}g P
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Resumen Diario */}
            {dailySummary && todayMeals?.completed_meals > 0 && (
              <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-5 h-5 text-[#D4AF37]" />
                    <h3 className="text-white font-bold">Resumen del Día</h3>
                    <Badge className="ml-auto bg-[#D4AF37]/20 text-[#D4AF37] border-none text-xs">
                      {todayMeals.completed_meals}/{todayMeals.total_meals} comidas
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Fila Objetivo */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#888]">Objetivo:</span>
                      <div className="flex gap-3">
                        <span className="text-white">{dailySummary.targets.calories} kcal</span>
                        <span className="text-red-400">{dailySummary.targets.protein}g P</span>
                        <span className="text-yellow-400">{dailySummary.targets.carbs}g C</span>
                        <span className="text-blue-400">{dailySummary.targets.fat}g G</span>
                      </div>
                    </div>
                    
                    {/* Fila Total Elegido */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#888]">Elegido:</span>
                      <div className="flex gap-3">
                        <span className="text-white font-bold">{Math.round(dailySummary.totals.calories)} kcal</span>
                        <span className="text-red-400 font-bold">{Math.round(dailySummary.totals.protein)}g P</span>
                        <span className="text-yellow-400 font-bold">{Math.round(dailySummary.totals.carbs)}g C</span>
                        <span className="text-blue-400 font-bold">{Math.round(dailySummary.totals.fat)}g G</span>
                      </div>
                    </div>
                    
                    {/* Fila Diferencia */}
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-[#2A2A2A]">
                      <span className="text-[#888]">Diferencia:</span>
                      <div className="flex gap-3">
                        <span className={cn(
                          "font-bold",
                          getDeviationColor(dailySummary.deviation.calories, THRESHOLDS.calories)
                        )}>
                          {dailySummary.deviation.calories > 0 ? '+' : ''}{Math.round(dailySummary.deviation.calories)} kcal
                        </span>
                        <span className={cn(
                          "font-bold",
                          getDeviationColor(dailySummary.deviation.protein, THRESHOLDS.protein)
                        )}>
                          {dailySummary.deviation.protein > 0 ? '+' : ''}{Math.round(dailySummary.deviation.protein)}g P
                        </span>
                        <span className={cn(
                          "font-bold",
                          getDeviationColor(dailySummary.deviation.carbs, THRESHOLDS.carbs)
                        )}>
                          {dailySummary.deviation.carbs > 0 ? '+' : ''}{Math.round(dailySummary.deviation.carbs)}g C
                        </span>
                        <span className={cn(
                          "font-bold",
                          getDeviationColor(dailySummary.deviation.fat, THRESHOLDS.fat)
                        )}>
                          {dailySummary.deviation.fat > 0 ? '+' : ''}{Math.round(dailySummary.deviation.fat)}g G
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicador visual de estado */}
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <Info className="w-3 h-3 text-[#666]" />
                    <span className="text-[#666]">
                      Verde = dentro del objetivo | Amarillo = desviación moderada | Rojo = desviación alta
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Modal Selector de Recetas */}
        <Dialog open={showRecipeSelector} onOpenChange={setShowRecipeSelector}>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
                <ChefHat className="w-5 h-5" />
                {selectedMealSlot?.slot_name} - Elegir Receta
              </DialogTitle>
            </DialogHeader>
            
            {/* Objetivo de la comida */}
            {selectedMealSlot?.target && (
              <div className="p-3 bg-[#1A1A1A] rounded-lg mb-3">
                <p className="text-[#888] text-xs mb-1">Objetivo de esta comida:</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#D4AF37] font-bold">{selectedMealSlot.target.calories} kcal</span>
                  <span className="text-red-400">{selectedMealSlot.target.protein}g P</span>
                  <span className="text-yellow-400">{selectedMealSlot.target.carbs}g C</span>
                  <span className="text-blue-400">{selectedMealSlot.target.fat}g G</span>
                </div>
              </div>
            )}
            
            {/* Lista de recetas */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {loadingRecipes ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-20 bg-[#1A1A1A] rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : availableRecipes.length === 0 ? (
                <div className="text-center py-8">
                  <ChefHat className="w-12 h-12 text-[#666] mx-auto mb-2" />
                  <p className="text-[#888]">No hay recetas disponibles para esta comida</p>
                </div>
              ) : (
                availableRecipes.map((recipe) => (
                  <Card 
                    key={recipe.id}
                    className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/50 cursor-pointer transition-all"
                    onClick={() => calculateRecipe(recipe)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div 
                        className="w-16 h-16 rounded-lg bg-cover bg-center flex-shrink-0"
                        style={{ backgroundImage: `url(${getRecipeImage(recipe)})` }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-semibold text-sm truncate">{recipe.nombre}</h4>
                        <div className="flex items-center gap-2 text-xs mt-1">
                          <span className="text-[#D4AF37]">{recipe.macros?.calorias || 0} kcal</span>
                          <span className="text-red-400">{recipe.macros?.proteinas || 0}g P</span>
                          {recipe.tiempo_preparacion && (
                            <span className="text-[#666] flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {recipe.tiempo_preparacion}
                            </span>
                          )}
                        </div>
                        {recipe.compatibility_score && (
                          <Badge className={cn(
                            "mt-1 text-[10px] border-none",
                            recipe.compatibility_score >= 80 ? "bg-green-500/20 text-green-400" :
                            recipe.compatibility_score >= 60 ? "bg-yellow-500/20 text-yellow-400" :
                            "bg-red-500/20 text-red-400"
                          )}>
                            {recipe.compatibility_score}% compatible
                          </Badge>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#666]" />
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Detalle de Receta Calculada */}
        <Dialog open={showRecipeDetail} onOpenChange={setShowRecipeDetail}>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#D4AF37]">
                {selectedRecipe?.nombre}
              </DialogTitle>
            </DialogHeader>
            
            {calculatedRecipe && (
              <div className="space-y-4">
                {/* Comparación de macros */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <p className="text-[#888] text-xs mb-2">Objetivo comida:</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-white">{calculatedRecipe.target_macros?.calories} kcal</p>
                      <p className="text-red-400">{calculatedRecipe.target_macros?.protein}g P</p>
                      <p className="text-yellow-400">{calculatedRecipe.target_macros?.carbs}g C</p>
                      <p className="text-blue-400">{calculatedRecipe.target_macros?.fat}g G</p>
                    </div>
                  </div>
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <p className="text-[#888] text-xs mb-2">Receta ajustada:</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-white font-bold">{Math.round(calculatedRecipe.final_macros?.calories || 0)} kcal</p>
                      <p className="text-red-400 font-bold">{Math.round(calculatedRecipe.final_macros?.protein || 0)}g P</p>
                      <p className="text-yellow-400 font-bold">{Math.round(calculatedRecipe.final_macros?.carbs || 0)}g C</p>
                      <p className="text-blue-400 font-bold">{Math.round(calculatedRecipe.final_macros?.fat || 0)}g G</p>
                    </div>
                  </div>
                </div>
                
                {/* Diferencia */}
                <div className={cn(
                  "p-3 rounded-lg",
                  getDeviationBg(calculatedRecipe.deviation?.calories || 0, THRESHOLDS.calories)
                )}>
                  <p className="text-[#888] text-xs mb-1">Diferencia respecto al objetivo:</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={getDeviationColor(calculatedRecipe.deviation?.calories || 0, THRESHOLDS.calories)}>
                      {(calculatedRecipe.deviation?.calories || 0) > 0 ? '+' : ''}{Math.round(calculatedRecipe.deviation?.calories || 0)} kcal
                    </span>
                    <span className={getDeviationColor(calculatedRecipe.deviation?.protein || 0, THRESHOLDS.protein)}>
                      {(calculatedRecipe.deviation?.protein || 0) > 0 ? '+' : ''}{Math.round(calculatedRecipe.deviation?.protein || 0)}g P
                    </span>
                    <span className={getDeviationColor(calculatedRecipe.deviation?.carbs || 0, THRESHOLDS.carbs)}>
                      {(calculatedRecipe.deviation?.carbs || 0) > 0 ? '+' : ''}{Math.round(calculatedRecipe.deviation?.carbs || 0)}g C
                    </span>
                    <span className={getDeviationColor(calculatedRecipe.deviation?.fat || 0, THRESHOLDS.fat)}>
                      {(calculatedRecipe.deviation?.fat || 0) > 0 ? '+' : ''}{Math.round(calculatedRecipe.deviation?.fat || 0)}g G
                    </span>
                  </div>
                </div>
                
                {/* Ingredientes ajustados */}
                <div>
                  <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                    <Apple className="w-4 h-4 text-[#D4AF37]" />
                    Ingredientes (cantidades personalizadas)
                  </h4>
                  <div className="bg-[#1A1A1A] rounded-lg p-3 space-y-2">
                    {calculatedRecipe.adjusted_ingredients?.filter(i => !i.is_extra).map((ing, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-white">{ing.food_name || ing.nombre}</span>
                        <span className="text-[#D4AF37] font-mono">
                          {formatIngredientQuantity(
                            ing.food_name || ing.nombre || '',
                            ing.adjusted_amount ?? ing.adjusted_quantity ?? ing.cantidad ?? 0,
                            ing.unit || ing.unidad || 'g'
                          )}
                        </span>
                      </div>
                    ))}
                    
                    {/* Alimentos añadidos para completar macros */}
                    {calculatedRecipe.has_extra_foods && (
                      <>
                        <div className="border-t border-[#2A2A2A] my-2 pt-2">
                          <p className="text-[#888] text-xs mb-2">+ Añadidos para completar macros:</p>
                        </div>
                        {calculatedRecipe.adjusted_ingredients?.filter(i => i.is_extra).map((ing, idx) => (
                          <div key={`extra-${idx}`} className="flex items-center justify-between text-sm">
                            <span className="text-green-400 flex items-center gap-1">
                              <Plus className="w-3 h-3" />
                              {ing.food_name}
                            </span>
                            <span className="text-green-400 font-mono">
                              {formatIngredientQuantity(
                                ing.food_name || '',
                                ing.adjusted_amount ?? 0,
                                ing.unit || 'g'
                              )}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Fit score */}
                <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                  <span className="text-[#888] text-sm">Puntuación de ajuste:</span>
                  <Badge className={cn(
                    "border-none",
                    (calculatedRecipe.fit_score || 0) >= 80 ? "bg-green-500/20 text-green-400" :
                    (calculatedRecipe.fit_score || 0) >= 60 ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  )}>
                    {Math.round(calculatedRecipe.fit_score || 0)}%
                  </Badge>
                </div>
                
                {/* Botones */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowRecipeDetail(false)}
                    className="flex-1 border-[#2A2A2A] text-white"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={saveRecipeSelection}
                    disabled={savingSelection}
                    className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                  >
                    {savingSelection ? "Guardando..." : "Elegir esta receta"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ModuleGate>
  );
};

export default MiNutricionPage;
